import React, { useState } from 'react'

import { ThemeProvider } from '@mui/material'
import { getTheme } from 'themes'

import { IDashboardExample } from 'types/GlobalDashboardExample'

import Box from 'components_new/atoms/Box'
import Button from 'components_new/atoms/Button'
import Icon from 'components_new/atoms/Icon'
import Text from 'components_new/atoms/Text'

import Link from 'components_new/atoms/Link'
import Chip from 'components_new/atoms/Chip'
import Tooltip from 'components_new/atoms/Tooltip'

import ActivateDashboardExampleDialog from './dialogs/ActivateDashboardExampleDialog'

import { getDashboardLimitReached } from 'utils/functions'
import { useAccountSettings } from 'redux/hooks/SignedInAccount'
import { useCustomer } from 'redux/hooks/Customer'
import { TFunction, useTranslation } from 'language/translator'
import { useSnackbarActions } from 'redux/actions/Snackbar'
import { SnackbarMessage } from 'types/GlobalSnackbar'

interface HeaderProps {
  dashboard: IDashboardExample
  sx?: object
}

const getDisabledTooltip = (
  allowCreateDashboard: boolean,
  dashboardLimitReached: boolean,
  noAccessMetrics: boolean,
  t: TFunction
) => {
  if (!allowCreateDashboard) {
    return t('dashboards', 'activate_disabled_no_permission')
  }

  if (dashboardLimitReached) {
    return t('dashboards', 'activate_disabled_limit_reached')
  }

  if (noAccessMetrics) {
    return t('dashboards', 'activate_disabled_no_access_metrics')
  }

  return undefined
}

const Header = (props: HeaderProps) => {
  const { dashboard, sx } = props
  const { allowCreateDashboard, isHomepal } = useAccountSettings()
  const customer = useCustomer()
  const { t, lang } = useTranslation()
  const { toggleSnackbarOpen } = useSnackbarActions()

  const createdDiff = Date.now() - Date.parse(dashboard.created_at)
  const createdDayDiff = Math.round(createdDiff / (1000 * 3600 * 24))
  const showNew = createdDayDiff < 30

  const updatedDiff = Date.now() - Date.parse(dashboard.updated_at)
  const updatedDayDiff = Math.round(updatedDiff / (1000 * 3600 * 24))
  const showUpdated = updatedDayDiff < 14 && !showNew

  const copyLinkToClipboard = () => {
    navigator.clipboard.writeText(location.href)

    toggleSnackbarOpen(SnackbarMessage.CopiedToClipboard)
  }

  const [activateDialogOpen, setActivateDialogOpen] = useState<boolean>(false)

  const noAccessMetrics = dashboard.data.metrics.filter(
    (metric) => !metric.access
  )
  const dashboardLimitReached = getDashboardLimitReached(isHomepal, customer)

  const disabledActivate =
    noAccessMetrics.length > 0 || !allowCreateDashboard || dashboardLimitReached

  const disabledActivateTooltip = getDisabledTooltip(
    allowCreateDashboard,
    dashboardLimitReached,
    noAccessMetrics.length > 0,
    t
  )

  return (
    <>
      <Box sx={sx}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            mb: 1
          }}
        >
          <Text variant="h2">{dashboard.title[lang]}</Text>
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              ml: 2
            }}
          >
            <Button
              onClick={copyLinkToClipboard}
              size="large"
              startIcon={<Icon name="LinkOutlined" />}
              variant="outlined"
            >
              {t('global', 'copy_link')}
            </Button>
            <Button
              component="a"
              endIcon={<Icon name="OpenInNewOutlined" />}
              href={`https://portal.homepal.se/public/dashboards/${dashboard.id}`}
              newTab={true}
              size="large"
              variant="outlined"
            >
              {t('dashboards', 'view_in_fullscreen')}
            </Button>
            <Tooltip title={disabledActivateTooltip}>
              <span>
                <Button
                  disabled={disabledActivate}
                  onClick={() => setActivateDialogOpen(true)}
                  size="large"
                  startIcon={<Icon name="AddCircleOutlineOutlined" />}
                  color="primary"
                >
                  {t('global', 'activate')}
                </Button>
              </span>
            </Tooltip>
          </Box>
        </Box>
        <Box sx={{ mb: 1 }}>
          {showNew ? <Chip label={`${t('global', 'new')} ✨`} /> : null}
          {showUpdated ? <Chip label={`${t('global', 'updated')} 🎉`} /> : null}
        </Box>
        <Text color="text.secondary" variant="caption">
          {t('global', 'created')}{' '}
          {new Date(dashboard.created_at).toLocaleDateString('sv')} •{' '}
          {t('global', 'updated')}{' '}
          {new Date(dashboard.updated_at).toLocaleDateString('sv')}
          {isHomepal ? (
            <>
              {' • '}
              <Tooltip
                title={`${dashboard.data.visits.unique} ${t(
                  'users',
                  dashboard.data.visits.unique === 1
                    ? 'unique_user'
                    : 'unique_users',
                  true
                )}`}
              >
                <Link sx={{ cursor: 'default' }}>
                  {dashboard.data.visits.total}{' '}
                  {t(
                    'global',
                    dashboard.data.visits.total === 1 ? 'view' : 'views',
                    true
                  )}
                </Link>
              </Tooltip>
            </>
          ) : null}
        </Text>
      </Box>

      {/*-- dialogs --*/}
      <ThemeProvider theme={getTheme('light')}>
        <ActivateDashboardExampleDialog
          dashboard={dashboard}
          onClose={() => setActivateDialogOpen(false)}
          open={activateDialogOpen}
        />
      </ThemeProvider>
    </>
  )
}

export default Header
